import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listApprovalRules,
  setApprovalRule,
  listApprovalRequests,
  listRequestsFor,
  requestApproval,
  requestCustomerEdit,
  approveRequest,
  rejectRequest,
  cancelRequest,
  type ApprovalStatus,
} from '@/lib/api/approvals';

const KEYS = {
  all: ['approvals'] as const,
  rules: () => [...KEYS.all, 'rules'] as const,
  requests: (status?: ApprovalStatus) => [...KEYS.all, 'requests', status ?? 'any'] as const,
  entity: (kind: string, id: string) => [...KEYS.all, 'entity', kind, id] as const,
};

export const useApprovalRules = () =>
  useQuery({ queryKey: KEYS.rules(), queryFn: listApprovalRules });

export const useApprovalRequests = (status?: ApprovalStatus) =>
  useQuery({
    queryKey: KEYS.requests(status),
    queryFn: () => listApprovalRequests(status),
    // Someone is standing at a till waiting for this to flip to approved.
    refetchInterval: 30_000,
  });

/** Requests of one kind about one entity (see listRequestsFor). */
export const useRequestsFor = (kind: string, key: string, id: string | null | undefined) =>
  useQuery({
    queryKey: KEYS.entity(kind, id ?? ''),
    queryFn: () => listRequestsFor(kind, key, id!),
    enabled: !!id,
    // The requester is looking at this page waiting for the answer.
    refetchInterval: 15_000,
  });

function useApprovalMutation<TArgs, TResult>(fn: (a: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export const useRequestApproval = () => useApprovalMutation(requestApproval);
export const useRequestCustomerEdit = () => useApprovalMutation(requestCustomerEdit);
export const useApproveRequest = () =>
  useApprovalMutation((a: { id: string; note?: string }) => approveRequest(a.id, a.note));
export const useRejectRequest = () =>
  useApprovalMutation((a: { id: string; note?: string }) => rejectRequest(a.id, a.note));
export const useCancelRequest = () =>
  useApprovalMutation((a: { id: string }) => cancelRequest(a.id));
export const useSetApprovalRule = () =>
  useApprovalMutation(
    (a: {
      kind: string;
      patch: { enabled?: boolean; requiredLevel?: number; threshold?: number | null };
    }) => setApprovalRule(a.kind, a.patch),
  );
